import React, { useState, useEffect } from 'react';
import {
  Row, Col, Form, FormGroup, Label, Input, Button, Table, Card, CardBody,
  Modal, ModalHeader, ModalBody, ModalFooter, Pagination, PaginationItem, PaginationLink,
  Spinner, Alert, Badge
} from 'reactstrap';
import axios from 'axios';
import { FaTrash, FaEdit, FaUpload, FaPlay, FaHeart, FaRegHeart, FaFire, FaImage, FaMusic } from 'react-icons/fa';

const ManageReels = () => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [hotTop, setHotTop] = useState(false);
  const [reels, setReels] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editVideoPreview, setEditVideoPreview] = useState(null);
  const [editThumbnailFile, setEditThumbnailFile] = useState(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState(null);
  const [editAudioFile, setEditAudioFile] = useState(null);
  const [editAudioPreview, setEditAudioPreview] = useState(null);
  const [editAudioDuration, setEditAudioDuration] = useState(0);
  const [editId, setEditId] = useState(null);
  const [editLikeCount, setEditLikeCount] = useState(0);
  const [editIsLiked, setEditIsLiked] = useState(false);
  const [editHotTop, setEditHotTop] = useState(false);

  // Video Modal State
  const [videoModal, setVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const reelsPerPage = 5;
  const totalPages = Math.ceil(reels.length / reelsPerPage);

  const paginatedReels = reels.slice(
    (currentPage - 1) * reelsPerPage,
    currentPage * reelsPerPage
  );

  const API_BASE_URL = 'https://api.editezy.com/api/admin';

  const fetchReels = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/getallreels`);
      setReels(res.data.reels || []);
    } catch (err) {
      console.error('Error fetching reels:', err);
      setError('Failed to fetch reels. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReels();
  }, []);

  // Validate audio duration (max 30 seconds)
  const validateAudioDuration = (file, callback) => {
    if (!file) return callback(true);
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    audio.src = objectUrl;
    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(objectUrl);
      const duration = audio.duration;
      if (duration > 30) {
        callback(false, duration);
      } else {
        callback(true, duration);
      }
    });
    audio.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      callback(false, 0);
    });
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('video/')) {
      setError('Please select a video file only');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Maximum file size is 50MB');
      return;
    }

    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('image/')) {
      setError('Please select an image file for thumbnail');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Maximum thumbnail size is 5MB');
      return;
    }

    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('audio/')) {
      setError('Please select an audio file (MP3, WAV, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Maximum audio file size is 10MB');
      return;
    }

    validateAudioDuration(file, (isValid, duration) => {
      if (!isValid) {
        setError(`Audio duration is ${duration.toFixed(1)} seconds. Maximum allowed is 30 seconds.`);
        setAudioFile(null);
        setAudioPreview(null);
        setAudioDuration(0);
      } else {
        setAudioFile(file);
        setAudioPreview(URL.createObjectURL(file));
        setAudioDuration(duration);
        setError(null);
      }
    });
  };

  const handleEditVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('video/')) {
      setError('Please select a video file only');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Maximum file size is 50MB');
      return;
    }

    setEditVideoFile(file);
    setEditVideoPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleEditThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('image/')) {
      setError('Please select an image file for thumbnail');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Maximum thumbnail size is 5MB');
      return;
    }

    setEditThumbnailFile(file);
    setEditThumbnailPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleEditAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('audio/')) {
      setError('Please select an audio file (MP3, WAV, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Maximum audio file size is 10MB');
      return;
    }

    validateAudioDuration(file, (isValid, duration) => {
      if (!isValid) {
        setError(`Audio duration is ${duration.toFixed(1)} seconds. Maximum allowed is 30 seconds.`);
        setEditAudioFile(null);
        setEditAudioPreview(null);
        setEditAudioDuration(0);
      } else {
        setEditAudioFile(file);
        setEditAudioPreview(URL.createObjectURL(file));
        setEditAudioDuration(duration);
        setError(null);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoFile) {
      setError('Please upload a video');
      return;
    }
    if (!thumbnailFile) {
      setError('Please upload a thumbnail image (Mandatory)');
      return;
    }
    if (audioFile && audioDuration > 30) {
      setError(`Audio duration exceeds 30 seconds (${audioDuration.toFixed(1)}s)`);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('thumbnail', thumbnailFile);
    if (audioFile) formData.append('audio', audioFile);
    formData.append('hotTop', hotTop);

    try {
      await axios.post(`${API_BASE_URL}/createreel`, formData);
      setSuccess('Reel created successfully!');
      fetchReels();
      setVideoFile(null);
      setVideoPreview(null);
      setThumbnailFile(null);
      setThumbnailPreview(null);
      setAudioFile(null);
      setAudioPreview(null);
      setAudioDuration(0);
      setHotTop(false);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error creating reel:', err);
      setError(err.response?.data?.message || 'Failed to create reel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reel?')) return;
    
    setError(null);
    setSuccess(null);
    
    try {
      await axios.delete(`${API_BASE_URL}/deletereel/${id}`);
      setSuccess('Reel deleted successfully!');
      fetchReels();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting reel:', err);
      setError(err.response?.data?.message || 'Failed to delete reel');
    }
  };

  const handleLikeToggle = async (reel) => {
    const formData = new FormData();
    const newLike = !reel.isLiked;
    const count = newLike ? reel.likeCount + 1 : Math.max(0, reel.likeCount - 1);

    formData.append('isLiked', newLike);
    formData.append('likeCount', count);
    formData.append('hotTop', reel.hotTop);

    try {
      await axios.put(`${API_BASE_URL}/updatereel/${reel._id}`, formData);
      fetchReels();
    } catch (err) {
      console.error('Error toggling like:', err);
      setError('Failed to update like status');
    }
  };

  const openEditModal = (reel) => {
    setEditId(reel._id);
    setEditLikeCount(reel.likeCount);
    setEditIsLiked(reel.isLiked);
    setEditHotTop(reel.hotTop || false);
    setEditVideoFile(null);
    setEditVideoPreview(null);
    setEditThumbnailFile(null);
    setEditThumbnailPreview(null);
    setEditAudioFile(null);
    setEditAudioPreview(null);
    setEditAudioDuration(0);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    const formData = new FormData();
    
    if (editVideoFile) formData.append('video', editVideoFile);
    if (editThumbnailFile) formData.append('thumbnail', editThumbnailFile);
    if (editAudioFile) formData.append('audio', editAudioFile);
    
    formData.append('likeCount', editLikeCount);
    formData.append('isLiked', editIsLiked);
    formData.append('hotTop', editHotTop);

    try {
      await axios.put(`${API_BASE_URL}/updatereel/${editId}`, formData);
      setSuccess('Reel updated successfully!');
      setEditModalOpen(false);
      fetchReels();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating reel:', err);
      setError(err.response?.data?.message || 'Failed to update reel');
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPagination = () => {
    const pages = [];
    const delta = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  };

  const openVideoModal = (url) => {
    setSelectedVideo(url);
    setVideoModal(true);
  };

  return (
    <div className="container my-4">
      {error && (
        <Alert color="danger" className="mb-4" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" className="mb-4" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row className="g-4">
        {/* Upload Section */}
        <Col xs={12} lg={5}>
          <Card className="shadow-sm border-0 h-100">
            <CardBody className="p-4">
              <h5 className="text-primary mb-3">
                <FaUpload className="me-2" /> Create New Reel
              </h5>

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label className="fw-bold">
                    Upload Video <span className="text-danger">*</span>
                  </Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <label className="btn btn-outline-primary btn-sm">
                      <FaUpload /> Choose Video
                      <Input type="file" hidden onChange={handleVideoChange} accept="video/*" required />
                    </label>
                    <span className="text-muted small">Max 50MB (MP4, MOV, etc.)</span>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Video will have "EDITEZY" watermark overlay automatically
                  </small>
                </FormGroup>

                <FormGroup className="mt-3">
                  <Label className="fw-bold">
                    <FaImage className="me-1" /> Upload Thumbnail <span className="text-danger">* (Mandatory)</span>
                  </Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <label className="btn btn-outline-primary btn-sm">
                      <FaImage /> Choose Thumbnail
                      <Input type="file" hidden onChange={handleThumbnailChange} accept="image/*" required={!thumbnailFile} />
                    </label>
                    <span className="text-muted small">Max 5MB (JPG, PNG, etc.)</span>
                  </div>
                  <small className="text-danger d-block mt-1">
                    ⚠️ Thumbnail is required. Please upload a thumbnail image for your reel.
                  </small>
                </FormGroup>

                {/* Audio Upload Field with 30 sec limit */}
                <FormGroup className="mt-3">
                  <Label className="fw-bold">
                    <FaMusic className="me-1" /> Background Audio (Optional)
                  </Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <label className="btn btn-outline-secondary btn-sm">
                      <FaMusic /> Choose Audio
                      <Input type="file" hidden onChange={handleAudioChange} accept="audio/*" />
                    </label>
                    <span className="text-muted small">MP3, WAV, etc. Max 10MB</span>
                  </div>
                  <small className="text-warning d-block mt-1">
                    ⏱️ ⚠️ Audio duration must be <strong>30 seconds or less</strong>. Longer audio will be rejected.
                  </small>
                  <small className="text-muted d-block">
                    If no audio is uploaded, the original video sound will be used (if any).
                  </small>
                </FormGroup>

                {audioPreview && (
                  <div className="mt-2">
                    <audio controls src={audioPreview} className="w-100" style={{ maxHeight: 50 }}>
                      Your browser does not support the audio element.
                    </audio>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <small className="text-muted">Duration: {audioDuration.toFixed(1)} sec</small>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setAudioFile(null);
                          setAudioPreview(null);
                          setAudioDuration(0);
                        }}
                      >
                        Remove Audio
                      </Button>
                    </div>
                  </div>
                )}

                {thumbnailPreview && (
                  <div className="mt-3">
                    <img
                      src={thumbnailPreview}
                      alt="Thumbnail preview"
                      className="rounded border"
                      style={{ width: '100%', maxHeight: 150, objectFit: 'cover' }}
                    />
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-1"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview(null);
                      }}
                    >
                      Remove Thumbnail
                    </Button>
                  </div>
                )}

                <FormGroup className="mt-3">
                  <Label className="d-flex align-items-center gap-2">
                    <FaFire className={hotTop ? "text-danger" : "text-muted"} />
                    Mark as Hot/Top Reel
                  </Label>
                  <div className="d-flex align-items-center gap-3">
                    <Input
                      type="switch"
                      checked={hotTop}
                      onChange={(e) => setHotTop(e.target.checked)}
                      style={{ width: '50px', height: '25px' }}
                    />
                    <span className={hotTop ? "text-danger fw-bold" : "text-muted"}>
                      {hotTop ? "Hot Reel - Will be highlighted" : "Normal Reel"}
                    </span>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Hot reels appear with a fire badge and get priority visibility
                  </small>
                </FormGroup>

                {videoPreview && (
                  <div className="mt-3">
                    <video
                      src={videoPreview}
                      controls
                      className="w-100 rounded border"
                      style={{ maxHeight: 250, objectFit: 'cover' }}
                    />
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        setVideoFile(null);
                        setVideoPreview(null);
                      }}
                    >
                      Remove Video
                    </Button>
                  </div>
                )}

                <Button 
                  color="primary" 
                  className="mt-3 w-100"
                  disabled={isSubmitting || !videoFile || !thumbnailFile}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Uploading...
                    </>
                  ) : (
                    'Create Reel'
                  )}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* Table Section */}
        <Col xs={12} lg={7}>
          <Card className="shadow-sm border-0 h-100">
            <CardBody className="p-3">
              <h5 className="mb-3 d-flex justify-content-between align-items-center">
                <span>All Reels ({reels.length})</span>
                {loading && <Spinner size="sm" color="primary" />}
              </h5>

              {loading && reels.length === 0 ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Loading reels...</p>
                </div>
              ) : reels.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>No reels found. Create your first reel!</p>
                </div>
              ) : (
                <>
                  <Table responsive hover className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Thumbnail/Video</th>
                        <th>Audio</th>
                        <th>Likes</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReels.map((reel, i) => (
                        <tr key={reel._id}>
                          <td>{(currentPage - 1) * reelsPerPage + i + 1}</td>
                          <td style={{ minWidth: 120 }}>
                            <div
                              style={{ position: 'relative', cursor: 'pointer' }}
                              onClick={() => openVideoModal(reel.videoUrl)}
                            >
                              <img
                                src={reel.thumbnailUrl}
                                alt="Reel thumbnail"
                                className="w-100 rounded"
                                style={{ height: 80, objectFit: 'cover' }}
                                onError={(e) => { e.target.src = ''; }}
                              />
                              <FaPlay
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: '#fff',
                                  fontSize: '18px',
                                  opacity: 0.8
                                }}
                              />
                            </div>
                          </td>
                          <td className="text-center">
                            {reel.audioUrl ? (
                              <span className="text-success" title="Has background audio">
                                <FaMusic size={18} />
                              </span>
                            ) : (
                              <span className="text-muted" title="No background audio">—</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <Button size="sm" color="link" onClick={() => handleLikeToggle(reel)} className="p-0">
                                {reel.isLiked ? <FaHeart className="text-danger" size={18} /> : <FaRegHeart size={18} />}
                              </Button>
                              <span className="fw-semibold">{reel.likeCount}</span>
                            </div>
                          </td>
                          <td>
                            {reel.hotTop && (
                              <Badge color="danger" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                <FaFire size={12} /> Hot
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button size="sm" color="primary" onClick={() => openEditModal(reel)}>
                                <FaEdit />
                              </Button>
                              <Button size="sm" color="danger" onClick={() => handleDelete(reel._id)}>
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {totalPages > 1 && (
                    <Pagination className="justify-content-center flex-wrap mt-3">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                      </PaginationItem>
                      {getPagination().map((p, i) => (
                        <PaginationItem key={i} active={p === currentPage} disabled={p === '...'}>
                          <PaginationLink onClick={() => p !== '...' && handlePageChange(p)}>
                            {p}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                      </PaginationItem>
                    </Pagination>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Edit Reel Modal */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setEditModalOpen(false)}>Edit Reel</ModalHeader>
        <Form onSubmit={handleEditSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Update Video (Optional)</Label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <label className="btn btn-outline-primary btn-sm">
                  <FaUpload /> Choose New Video
                  <Input type="file" hidden onChange={handleEditVideoChange} accept="video/*" />
                </label>
                <span className="text-muted small">Leave empty to keep current video</span>
              </div>
              {editVideoPreview && (
                <div className="mt-2">
                  <video src={editVideoPreview} controls className="w-100 rounded" style={{ maxHeight: 150, objectFit: 'cover' }} />
                  <Button color="danger" size="sm" className="mt-1" onClick={() => {
                    setEditVideoFile(null);
                    setEditVideoPreview(null);
                  }}>Remove New Video</Button>
                </div>
              )}
            </FormGroup>

            <FormGroup className="mt-3">
              <Label>Update Thumbnail (Optional)</Label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <label className="btn btn-outline-secondary btn-sm">
                  <FaImage /> Choose New Thumbnail
                  <Input type="file" hidden onChange={handleEditThumbnailChange} accept="image/*" />
                </label>
                <span className="text-muted small">Leave empty to keep current thumbnail</span>
              </div>
              {editThumbnailPreview && (
                <div className="mt-2">
                  <img src={editThumbnailPreview} alt="Thumbnail preview" className="rounded border" style={{ width: '100%', maxHeight: 150, objectFit: 'cover' }} />
                  <Button color="danger" size="sm" className="mt-1" onClick={() => {
                    setEditThumbnailFile(null);
                    setEditThumbnailPreview(null);
                  }}>Remove New Thumbnail</Button>
                </div>
              )}
            </FormGroup>

            <FormGroup className="mt-3">
              <Label>Update Background Audio (Optional, ≤30 sec)</Label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <label className="btn btn-outline-warning btn-sm">
                  <FaMusic /> Choose New Audio
                  <Input type="file" hidden onChange={handleEditAudioChange} accept="audio/*" />
                </label>
                <span className="text-muted small">Leave empty to keep existing audio</span>
              </div>
              <small className="text-warning d-block mt-1">
                ⏱️ Audio must be ≤30 seconds. Longer files will be rejected.
              </small>
              {editAudioPreview && (
                <div className="mt-2">
                  <audio controls src={editAudioPreview} className="w-100" />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className="text-muted">Duration: {editAudioDuration.toFixed(1)} sec</small>
                    <Button color="danger" size="sm" onClick={() => {
                      setEditAudioFile(null);
                      setEditAudioPreview(null);
                      setEditAudioDuration(0);
                    }}>Remove New Audio</Button>
                  </div>
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Like Count</Label>
              <Input type="number" value={editLikeCount} onChange={(e) => setEditLikeCount(parseInt(e.target.value) || 0)} min="0" />
            </FormGroup>

            <FormGroup check>
              <Label check>
                <Input type="checkbox" checked={editIsLiked} onChange={(e) => setEditIsLiked(e.target.checked)} />{' '}
                Liked Status
              </Label>
            </FormGroup>

            <FormGroup check className="mt-2">
              <Label check className="d-flex align-items-center gap-2">
                <Input type="checkbox" checked={editHotTop} onChange={(e) => setEditHotTop(e.target.checked)} />{' '}
                <FaFire className="text-danger" /> Mark as Hot/Top Reel
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button color="primary" type="submit">Update Reel</Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Video Preview Modal */}
      <Modal isOpen={videoModal} toggle={() => setVideoModal(false)} centered size="lg">
        <ModalHeader toggle={() => setVideoModal(false)}>Reel Preview</ModalHeader>
        <ModalBody className="text-center p-2">
          {selectedVideo && (
            <video src={selectedVideo} controls autoPlay className="w-100 rounded" style={{ maxHeight: '70vh', objectFit: 'contain' }} />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ManageReels;